import React, { useMemo, useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Table,
  Select,
  DatePicker,
  Button,
  Statistic,
  Empty,
  Spin,
  Input,
} from 'antd'
import {
  AppstoreOutlined,
  TableOutlined,
  DollarOutlined,
  FileDoneOutlined,
  ReloadOutlined,
  SearchOutlined,
  DatabaseOutlined,
  InboxOutlined,
} from '@ant-design/icons'

import * as stockApi from '../../../../../api/stockApi'


import { notify } from '../../../../components/Customs/NotifyProvider'
import './StockReport.css'
import { canViewModule, canCreateModule, canUpdateModule, canDeleteModule } from '../../../../utils/permissionUtils'

const { RangePicker } = DatePicker

const StockReport = ({drinks =[],stockLogsMap = {}}) => {
  const MODULE_KEY = 'stock_report'
  const canView = canViewModule(MODULE_KEY)
  const canCreate = canCreateModule(MODULE_KEY)
  const canUpdate = canUpdateModule(MODULE_KEY)
  const canDelete = canDeleteModule(MODULE_KEY)
  const isViewOnly = canView && !canCreate && !canUpdate && !canDelete
  const [loading, setLoading] = useState(false)
  const [rawData, setRawData] = useState([])
  const [dateFilter, setDateFilter] = useState('today')
  const [range, setRange] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('All')
  const [searchText, setSearchText] = useState('')

  //color
  const iconStyle = { color: '#7c3aed', fontSize: '22px' }

  const buildFilters = () => {
    if (isViewOnly) return { date: 'today' }
    const filters = {}

    if (dateFilter === 'custom' && range && range.length === 2) {
      filters.from = range[0].startOf('day').format('YYYY-MM-DD HH:mm:ss')
      filters.to = range[1].endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else if (dateFilter === 'today') {
      filters.date = 'today'
    } else if (dateFilter === 'yesterday') {
      filters.date = 'yesterday'
    }

    if (categoryFilter !== 'All') {
      filters.category = categoryFilter
    }

    return filters
  }

  const fetchData = async () => {
  if (!canView) return
  setLoading(true)

  try {
    const filters = buildFilters()

    const res = await stockApi.getStockReport(filters)

    const rows = res?.data || []

    console.log("API DATA:", rows)

    setRawData(rows)
  } catch (err) {
    notify.error(`Failed to load stock report: ${err.message}`)
    setRawData([])
  } finally {
    setLoading(false)
  }
}


//auto today dispaly
useEffect(() => {
  fetchData()
}, [])


  const handleClear = () => {
    if (!canUpdate && !canDelete) {
      notify.error('Permission Denied', 'No permission to clear stock report filters')
      return
    }
    setDateFilter('today')
    setRange(null)
    setCategoryFilter('All')
    setSearchText('')
    setRawData([])
     
  }

  const categoryOptions = useMemo(() => {
    const cats = rawData.map((i) => i.category).filter(Boolean)
    return ['All', ...new Set(cats)]
  }, [rawData])

 const tableData = useMemo(() => {
  const search = searchText.toLowerCase()

  return rawData
    .map((l, index) => ({
      key: index,

      item_name: l.item_name || 'Unknown',
      category: l.category || 'Unknown',

      box_qty: l.box_qty || 0,
      bottle_qty: l.bottle_qty || 0,
      units_per_box: l.units_per_box || 0,

      total_bottle: (l.box_qty * (l.units_per_box || 0)) + (l.bottle_qty || 0),

      price: l.price_per_bottle || 0,
      total_value:
        ((l.box_qty * (l.units_per_box || 0)) + (l.bottle_qty || 0)) *
        (l.price_per_bottle || 0),

      paid_amount: l.paid_amount || 0,
    }))
    .filter((item) =>
      search
        ? Object.values(item).join(' ').toLowerCase().includes(search)
        : true
    )
}, [rawData, searchText])

const summary = useMemo(() => {
  let totalBox = 0
  let totalBottle = 0
  let totalPaid = 0
  let revenue = 0

  rawData.forEach((l) => {
    const box = Number(l.box_qty || 0)
    const bottle = Number(l.bottle_qty || 0)
    const units = Number(l.units_per_box || 0)
    const price = Number(l.price_per_bottle || 0)

    const totalUnits = (box * units) + bottle

    totalBox += box
    totalBottle += totalUnits
    totalPaid += Number(l.paid_amount || 0)
    revenue += totalUnits * price
  })

  return {
    totalItems: rawData.length,
    totalBox,
    totalBottle,
    totalPaid,
    revenue,
  }
}, [rawData])

  const columns = [
  {
    title: 'Item',
    dataIndex: 'item_name',
  },
  {
    title: 'Category',
    dataIndex: 'category',
  },
  {
    title: 'Box Qty',
    dataIndex: 'box_qty',
  },
  {
    title: 'Bottle Qty',
    dataIndex: 'bottle_qty',
  },
  {
    title: 'Units/Box',
    dataIndex: 'units_per_box',
  },
  {
    title: 'Total Bottles',
    dataIndex: 'total_bottle',
  },
  {
    title: 'Price/Bottle',
    dataIndex: 'price',
    render: (v) => `₹${v}`,
  },
 
  {
    title: 'Paid Amount',
    dataIndex: 'paid_amount',
    render: (v) => `₹${v}`,
  },
]
  return !canView ? (
    <Card>
      <Empty description="No permission to view stock report" />
    </Card>
  ) : (
    <div className="stock-report-page">

      {/* HERO */}
      <div className="stock-report-hero">
        <div className="stock-report-hero-left">
          <div className="stock-report-hero-icon">
            <DatabaseOutlined />
          </div>
          <div>
            <h2 className="stock-report-hero-title">Stock Report</h2>
            <p className="stock-report-hero-subtitle">
              Inventory analytics and stock valuation overview
            </p>
          </div>
        </div>
      </div>

      <Card className="stock-report-card">

        {/* FILTERS */}
        <Row gutter={[12, 12]} className="stock-report-filters">

          <Col xs={24} sm={12} md={6}>
            <Select
              value={dateFilter}
              onChange={setDateFilter}
              disabled={isViewOnly}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'All', value: 'all' },
                { label: 'Custom', value: 'custom' },
              ]}
            />
          </Col>

          {dateFilter === 'custom' && !isViewOnly && (
            <Col xs={24} sm={12} md={8}>
              <RangePicker style={{ width: '100%' }} value={range} onChange={setRange} />
            </Col>
          )}

          <Col xs={24} sm={12} md={6}>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              disabled={isViewOnly}
              options={categoryOptions.map((c) => ({ label: c, value: c }))}
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={3}>
                     <Button
                       type="primary"
                       block
                       icon={<ReloadOutlined />}
                       className="table-report-load-btn"
                       onClick={fetchData}
                       loading={loading}
                       disabled={!canView && !canCreate}
                     >
                       Load
                     </Button>
                   </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
                      <Button
                        block
                        className="table-report-clear-btn"
                        onClick={handleClear}
                        disabled={!canUpdate && !canDelete}
                      >
                        Clear
                      </Button>
                    </Col>
        </Row>

        {/* SEARCH */}
        <div className="stock-report-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search stock..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="stock-search"
          />
        </div>

        {/* STATS */}
   <Row gutter={[16, 16]} className="stock-report-stats">

  <Col xs={24} sm={12} md={8} lg={8} xl={8}>
    <Card className="stock-stat-card"   
    style={{
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
    borderRadius: "12px"
     }}>
      <Statistic
        title="Total Boxes"
        value={summary.totalBox}
        prefix={<InboxOutlined  style={iconStyle}/>}
      />
    </Card>
  </Col>

  <Col xs={24} sm={12} md={8} lg={8} xl={8}>
    <Card className="stock-stat-card"   
     style={{
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
    borderRadius: "12px"
    }}>
      <Statistic
        title="Total Bottles"
        value={summary.totalBottle}
        prefix={<AppstoreOutlined style={iconStyle} />}
      />
    </Card>
  </Col>

  <Col xs={24} sm={12} md={8} lg={8} xl={8}>
    <Card className="stock-stat-card" 
    style={{
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
    borderRadius: "12px"
     }}>
      <Statistic
        title="Total Spent Amount"
        value={summary.totalPaid}
        prefix={<DatabaseOutlined style={iconStyle} /> }
        formatter={(v) => `₹${Number(v).toFixed(2)}`}
      />
    </Card>
  </Col>

  

</Row>
        {/* TABLE */}
        <Spin spinning={loading}>
          {!tableData.length ? (
            <Empty />
          ) : (
            <div className="stock-table">
              <Table
                rowKey="stock_id"
                columns={columns}
                dataSource={tableData}
                pagination={{ pageSize: 10 }}
              />
            </div>
          )}
        </Spin>

      </Card>
    </div>
  )
}

export default StockReport
